import React, { useCallback } from 'react';
import { FormikProvider, Form, useFormik } from 'formik';
import { Modal, Button, Input, Stack, ButtonGroup, Text, Loader, Banner } from '@chaser/components';
import { useHistory } from 'react-router';
import ConnectService from '../../../../services/connect-service';
import debounce from 'lodash/debounce';
import usePush from '../../../../hooks/usePush';
import useOnUpdate from '../../../../hooks/useOnUpdate';
import LabeledFieldWithError from '../../../../components/LabeledFieldWithError/index.jsx';
import { bool, func } from 'prop-types';
import { pendo } from '../../../../util/logging';

const StripeModal = ({ open, close, onClosed }) => {
  const history = useHistory();

  const { execute: previewStripe, loading: isPreviewLoading, error, response: preview } = usePush(
    ConnectService.previewStripe,
  );

  const { execute: connectStripe, loading: isConnectLoading } = usePush(
    ConnectService.connectStripe,
    {
      successToast: 'Successfully connected to Stripe',
      errorToast: 'Failed to connect to Stripe',
    },
  );

  const form = useFormik({
    initialValues: {
      stripeToken: '',
    },
    validate: ({ stripeToken }) => {
      const errors = {};
      if (!stripeToken) errors.stripeToken = 'Please enter a Stripe API token';
      return errors;
    },
    validateOnMount: true,
    onSubmit: async ({ stripeToken }) => {
      const [err, data] = await connectStripe({ stripeToken });
      if (err) return;

      close();
      pendo.track('integration_modal_submitted', {
        integration: 'stripe',
      });
      history.push(`/?organisation=${data.ShortCode}`);
    },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderPreview = useCallback(
    debounce(token => {
      if (!token) return;
      previewStripe({ stripeToken: token });
    }, 500),
    [],
  );

  useOnUpdate(() => {
    if (form.values.stripeToken) {
      renderPreview(form.values.stripeToken);
    }
  }, [form.values.stripeToken]);

  return (
    <Modal title="Connect to Stripe" open={open} toggle={close} onClosed={onClosed}>
      <FormikProvider value={form}>
        <Form>
          <Modal.Body>
            <Stack gap="medium">
              <Banner intent="info" marginBottom={12} showCloseButton={false}>
                You'll need a <b>restricted API key</b> from your Stripe account. Learn how to
                create one{' '}
                <a
                  href="https://help.chaserhq.com/use-chaser-with-stripe"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <u>here</u>
                </a>
              </Banner>
              <LabeledFieldWithError
                label="Enter your Stripe restricted API key to connect your account"
                name="stripeToken"
                as={Input}
                type="text"
                placeholder="e.g. rk_live_12345xxxxxxxxxxxx..."
                width="100%"
                loading={isPreviewLoading}
              />

              {error && <Text color="Red">{error}</Text>}

              {isPreviewLoading && <Loader />}

              {preview && (
                <Stack gap="small">
                  <Text>
                    Connect to:{` `}
                    <Text fontWeight="Bold">{preview.organisationName}</Text>
                  </Text>
                </Stack>
              )}
            </Stack>
          </Modal.Body>

          <Modal.Footer>
            <ButtonGroup>
              <Button onClick={close} appearance="secondary" type="button" data-trackingid="integration-stripe-cancel">
                Cancel
              </Button>
              <Button disabled={!form.isValid} loading={isConnectLoading} type="submit" data-trackingid="integration-stripe-connect">
                Connect
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

StripeModal.propTypes = {
  open: bool.isRequired,
  close: func.isRequired,
  onClosed: func,
};

export default StripeModal;
